import { useActiveScopeCapabilities } from "@/hooks";
import { KnownCapability, ScopeLevel } from "@/types/capability.types";
import { hasCapability } from "@/utils/capability";

// The album owner scope (league > organization > platform, see useAlbumOwner)
// matches the active scope level 1:1 — the league arm therefore reads the
// league set on `/league/:id/gallery` routes. Keys are scope-exact on purpose:
// the backend enforces `<LEVEL>_GALLERY:*` at the owner node without
// implied-capability expansion.
const ALBUM_CREATE_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_GALLERY:CREATE",
  organization: "ORG_GALLERY:CREATE",
  league: "LEAGUE_GALLERY:CREATE",
};

const ALBUM_WRITE_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_GALLERY:WRITE",
  organization: "ORG_GALLERY:WRITE",
  league: "LEAGUE_GALLERY:WRITE",
};

const ALBUM_DELETE_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_GALLERY:DELETE",
  organization: "ORG_GALLERY:DELETE",
  league: "LEAGUE_GALLERY:DELETE",
};

/**
 * Write permissions for the gallery/albums surface at the active scope
 * (`<LEVEL>_GALLERY:CREATE` / `WRITE` / `DELETE` on the active scope's set).
 * Strict RBAC — the S0 grants give every legacy member the matching keys. The
 * list itself stays open by design — there is no `*_GALLERY:READ` key.
 */
export const useAlbumPermissions = () => {
  const { level, capabilities } = useActiveScopeCapabilities();

  return {
    canCreateAlbum: hasCapability(capabilities, ALBUM_CREATE_KEYS[level]),
    canUpdateAlbum: hasCapability(capabilities, ALBUM_WRITE_KEYS[level]),
    canDeleteAlbum: hasCapability(capabilities, ALBUM_DELETE_KEYS[level]),
  };
};
